import React, { useState, useEffect, useRef, useCallback } from "react";

import api from "../api";
import { selectSuggestions } from "../ariaSuggestions";
import { useModules } from "../hooks/useModules";
import { useActiveModule } from "../useActiveModule";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";
const GREEN = "#16a34a";
const RED   = "#dc2626";

// ── Types ─────────────────────────────────────────────────────────────────────

interface TextMessage {
  id: string;
  kind: "text";
  role: "user" | "assistant";
  content: string;
}

interface ActionCardMessage {
  id: string;
  kind: "action_card";
  role: "assistant";
  action_type: string;
  action_label: string;
  confirmation_text: string;
  params: Record<string, unknown>;
  preview_details: Record<string, unknown>;
  deep_link: string;
  state?: "pending" | "confirmed" | "cancelled" | "error";
  result_summary?: string;
}

type Message = TextMessage | ActionCardMessage;

// FastAPI returns `detail` as a string for HTTPException, but as an array of
// Pydantic error objects ({loc, msg, type}) for 422 validation errors — naively
// interpolating that into a template string renders "[object Object]".
function _errDetail(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d))).join("; ");
  }
  return typeof detail === "object" ? (detail.msg || JSON.stringify(detail)) : String(detail);
}

function _errCode(e: any): string | undefined {
  const detail = e?.response?.data?.detail;
  return typeof detail === "object" && detail !== null ? detail.code : undefined;
}

// Suggestion chips live in ../ariaSuggestions — role and entitlement decide which
// are offered, and the module the user is standing on decides their order.

// ── Preview detail label map (Fix 12) ─────────────────────────────────────────

const PREVIEW_LABELS: Record<string, string> = {
  employee:   "Employee",
  leave_type: "Leave Type",
  start:      "Start Date",
  end:        "End Date",
  days:       "Days",
  title:      "Title",
  amount:     "Amount",
  currency:   "Currency",
  body:       "Message",
  candidate:     "Candidate",
  scheduled_at:  "Scheduled At",
  date:          "Date",
  time:          "Time",
  type:          "Type",
  duration:      "Duration",
  current_stage: "Current Stage",
  new_stage:     "New Stage",
  job:           "Job",
  ticket_id:     "Ticket #",
  subject:       "Subject",
};

function previewLabel(key: string): string {
  return PREVIEW_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// ── Simple markdown renderer (Fix output polish) ──────────────────────────────

function AriaMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let listBuffer: string[] = [];

  function flushList() {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={`ul-${elements.length}`} style={{ margin: "4px 0 4px 16px", padding: 0 }}>
        {listBuffer.map((item, i) => (
          <li key={i} style={{ marginBottom: 2 }}>{renderInline(item)}</li>
        ))}
      </ul>
    );
    listBuffer = [];
  }

  function renderInline(raw: string): React.ReactNode[] {
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(raw)) !== null) {
      if (m.index > last) parts.push(raw.slice(last, m.index));
      if (m[2] !== undefined) parts.push(<strong key={m.index}>{m[2]}</strong>);
      else if (m[3] !== undefined) parts.push(<em key={m.index}>{m[3]}</em>);
      last = m.index + m[0].length;
    }
    if (last < raw.length) parts.push(raw.slice(last));
    return parts;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (/^[-•*]\s+/.test(trimmed)) {
      listBuffer.push(trimmed.replace(/^[-•*]\s+/, ""));
    } else {
      flushList();
      if (trimmed === "") {
        if (i < lines.length - 1) elements.push(<br key={`br-${i}`} />);
      } else {
        elements.push(
          <span key={`line-${i}`} style={{ display: "block" }}>{renderInline(trimmed)}</span>
        );
      }
    }
  }
  flushList();
  return <div style={{ fontSize: 13, lineHeight: 1.6, color: "#1e293b" }}>{elements}</div>;
}

// ── Action icon ───────────────────────────────────────────────────────────────

function ActionIcon({ action_type }: { action_type: string }) {
  const icons: Record<string, string> = {
    approve_leave:    "✅", reject_leave:     "❌", apply_leave:     "📅",
    approve_expense:  "💰", reject_expense:   "🚫",
    schedule_interview:"🗓", cancel_interview: "📵",
    advance_candidate: "⏩",
    post_announcement:"📢", resolve_helpdesk: "🔧",
  };
  return <span>{icons[action_type] || "⚡"}</span>;
}

// ── Action card component ─────────────────────────────────────────────────────

function ActionCard({
  msg,
  onConfirm,
  onCancel,
  confirming,
}: {
  msg: ActionCardMessage;
  onConfirm: () => void;
  onCancel: () => void;
  confirming: boolean;
}) {
  const isSettled = msg.state === "confirmed" || msg.state === "cancelled" || msg.state === "error";

  return (
    <div style={{
      background: "white", border: `1.5px solid ${NAVY}22`,
      borderRadius: 12, padding: "12px 14px", maxWidth: "90%",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <ActionIcon action_type={msg.action_type} />
        <span style={{ fontSize: 12, fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {msg.action_label}
        </span>
        {msg.state === "confirmed" && <span style={{ marginLeft: "auto", fontSize: 11, color: GREEN, fontWeight: 600 }}>Done ✓</span>}
        {msg.state === "cancelled" && <span style={{ marginLeft: "auto", fontSize: 11, color: "#64748b" }}>Cancelled</span>}
        {msg.state === "error"     && <span style={{ marginLeft: "auto", fontSize: 11, color: RED, fontWeight: 600 }}>Failed</span>}
      </div>

      {!isSettled ? (
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "#1e293b", lineHeight: 1.5 }}>
          {msg.confirmation_text}
        </p>
      ) : (
        <p style={{ margin: "0 0 6px", fontSize: 13, color: msg.state === "error" ? RED : "#1e293b", lineHeight: 1.5 }}>
          {msg.result_summary}
        </p>
      )}

      {!isSettled && Object.keys(msg.preview_details).length > 0 && (
        <div style={{
          background: "#f8fafc", borderRadius: 8, padding: "8px 10px",
          marginBottom: 10, fontSize: 12, color: "#475569", lineHeight: 1.6,
        }}>
          {Object.entries(msg.preview_details).map(([k, v]) => (
            <div key={k}>
              <span style={{ fontWeight: 600, color: "#334155" }}>{previewLabel(k)}: </span>
              <span>{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {!isSettled ? (
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8, border: "none",
              cursor: confirming ? "not-allowed" : "pointer",
              background: confirming ? "#94a3b8" : `linear-gradient(135deg, ${NAVY}, #1e40af)`,
              color: "white", fontSize: 13, fontWeight: 600,
            }}
          >
            {confirming ? "Confirming…" : "Yes, confirm"}
          </button>
          <button
            onClick={onCancel}
            disabled={confirming}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8, border: "1.5px solid #e2e8f0",
              cursor: confirming ? "not-allowed" : "pointer",
              background: "white", color: "#64748b", fontSize: 13,
            }}
          >
            Cancel
          </button>
        </div>
      ) : msg.state === "confirmed" ? (
        <a href={msg.deep_link} style={{
          display: "block", textAlign: "center", fontSize: 12, color: NAVY,
          textDecoration: "none", fontWeight: 600, marginTop: 4,
        }}>
          View in app →
        </a>
      ) : null}
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────

export default function ChatWidget({ role, userName }: { role: string; userName?: string }) {
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [quota, setQuota]     = useState<{ used: number; limit: number; remaining: number | null } | null>(null);
  const [ariaUnread, setAriaUnread] = useState(0);
  const [planRestricted, setPlanRestricted] = useState(false);
  const bottomRef  = useRef<HTMLDivElement>(null);
  const inputRef   = useRef<HTMLInputElement>(null);

  // Read here rather than passed down from ShellLayout: the widget is mounted in
  // exactly one place, and threading two more props through the layout to reach it
  // would put the knowledge of what Aria needs in the layout instead of in Aria.
  const { modules } = useModules();
  // The shell's notion of context rather than the raw path, so Aria agrees with
  // the sidebar and the badge on the System pages (Settings/Workspace/Security),
  // which stay in the module they were opened from.
  const activeModule = useActiveModule();

  const FAB_SIZE = 56;
  const EDGE_MARGIN = 8;
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number }>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("aria_fab_pos") || "null");
      if (saved && typeof saved.right === "number" && typeof saved.bottom === "number") return saved;
    } catch {}
    return { right: 24, bottom: 24 };
  });
  const dragRef = useRef<{ startX: number; startY: number; startRight: number; startBottom: number; dragging: boolean } | null>(null);
  const draggedRef = useRef(false);

  function clampFabPos(right: number, bottom: number) {
    const maxRight = Math.max(EDGE_MARGIN, window.innerWidth - FAB_SIZE - EDGE_MARGIN);
    const maxBottom = Math.max(EDGE_MARGIN, window.innerHeight - FAB_SIZE - EDGE_MARGIN);
    return {
      right: Math.min(Math.max(right, EDGE_MARGIN), maxRight),
      bottom: Math.min(Math.max(bottom, EDGE_MARGIN), maxBottom),
    };
  }

  useEffect(() => {
    function onResize() { setFabPos(p => clampFabPos(p.right, p.bottom)); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function handleFabPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startRight: fabPos.right, startBottom: fabPos.bottom, dragging: false };
  }

  function handleFabPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.dragging && Math.hypot(dx, dy) < 4) return;
    d.dragging = true;
    draggedRef.current = true;
    setFabPos(clampFabPos(d.startRight - dx, d.startBottom - dy));
  }

  function handleFabPointerUp() {
    const d = dragRef.current;
    dragRef.current = null;
    if (d?.dragging) {
      setFabPos(p => {
        try { localStorage.setItem("aria_fab_pos", JSON.stringify(p)); } catch {}
        return p;
      });
    }
  }

  function handleFabClick() {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setOpen(o => !o);
  }

  const panelWidth = 370;
  const panelHeight = 540;
  const panelRight = Math.min(Math.max(fabPos.right, EDGE_MARGIN), Math.max(EDGE_MARGIN, window.innerWidth - panelWidth - EDGE_MARGIN));
  const panelBottom = Math.min(Math.max(fabPos.bottom + FAB_SIZE + 12, EDGE_MARGIN), Math.max(EDGE_MARGIN, window.innerHeight - panelHeight - EDGE_MARGIN));

  const isUnlimited  = quota != null && quota.remaining === null;
  const firstName    = (userName || "").split(" ")[0] || "";
  const hasPendingCard = messages.some(m => m.kind === "action_card" && m.state === "pending");
  const outOfQuota   = !isUnlimited && quota?.remaining === 0;

  function newId() { return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`; }

  const fetchQuota = useCallback(async () => {
    try {
      const r = await api.get("/api/chat/quota");
      setQuota(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchQuota();
      setTimeout(() => inputRef.current?.focus(), 100);

      // Load persistent Aria milestone messages and prepend to chat history
      api.get("/api/chat/aria-messages").then((r: any) => {
        const { messages: ariaMsgs, unread } = r.data as {
          messages: { id: number; text: string; trigger_type: string | null; read: boolean; created_at: string }[];
          unread: number;
        };
        if (ariaMsgs.length > 0) {
          const injected: TextMessage[] = ariaMsgs.map(m => ({
            id: `aria-milestone-${m.id}`,
            kind: "text" as const,
            role: "assistant" as const,
            content: m.text,
          }));
          setMessages(prev => {
            // Avoid re-injecting on every open — skip if already present
            const existingIds = new Set(prev.map(p => p.id));
            const fresh = injected.filter(m => !existingIds.has(m.id));
            if (fresh.length === 0) return prev;
            return [...fresh, ...prev];
          });
        }
        setAriaUnread(0);
        // Mark read on the server
        if (unread > 0) api.post("/api/chat/aria-messages/read").catch(() => {});
      }).catch(() => {});
    }
  }, [open, fetchQuota]);

  // Refresh quota + unread dot when tab regains focus
  useEffect(() => {
    function onVisible() {
      if (document.hidden) return;
      if (open) { fetchQuota(); return; }
      // Widget closed — just refresh the unread count for the dot
      api.get("/api/chat/aria-messages").then((r: any) => {
        setAriaUnread(r.data.unread ?? 0);
      }).catch(() => {});
    }
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [open, fetchQuota]);

  // Poll unread count on mount (shows dot even before first open)
  useEffect(() => {
    api.get("/api/chat/aria-messages").then((r: any) => {
      setAriaUnread(r.data.unread ?? 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function send(text?: string) {
    const msg = (text || input).trim();
    if (!msg || loading || hasPendingCard) return; // Fix 8: block while card pending
    if (outOfQuota) return;

    setInput("");
    const userMsg: TextMessage = { id: newId(), kind: "text", role: "user", content: msg };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const r = await api.post("/api/chat", {
        message: msg,
        history: messages
          .filter((m): m is TextMessage => m.kind === "text")
          .slice(-6)
          .map(m => ({ role: m.role, content: m.content })),
        // Where the user is, as a tie-breaker for ambiguous questions. Advisory —
        // the backend drops it if the tenant does not own it, and it never narrows
        // what Aria will answer. Null in Core.
        module: activeModule,
      });

      if (r.data.type === "action" && Array.isArray(r.data.cards)) {
        // A single request can produce more than one action card (e.g. "approve John's
        // leave and post an announcement about it") — each still needs its own confirmation.
        const cards: ActionCardMessage[] = r.data.cards.map((c: any) => ({
          id: newId(),
          kind: "action_card",
          role: "assistant",
          action_type: c.action_type,
          action_label: c.action_label,
          confirmation_text: c.confirmation_text,
          params: c.params,
          preview_details: c.preview_details || {},
          deep_link: c.deep_link,
          state: "pending",
        }));
        setMessages(prev => [...prev, ...cards]);
      } else {
        setMessages(prev => [...prev, { id: newId(), kind: "text", role: "assistant", content: r.data.answer }]);
      }

      if (r.data.remaining != null) {
        setQuota(q => q ? { ...q, used: r.data.used, remaining: r.data.remaining } : null);
      }
    } catch (e: any) {
      if (_errCode(e) === "plan_required") {
        setPlanRestricted(true);
        return;
      }
      const detail = _errDetail(e, "Something went wrong. Please try again.");
      setMessages(prev => [...prev, { id: newId(), kind: "text", role: "assistant", content: `⚠️ ${detail}` }]);
    } finally {
      setLoading(false);
    }
  }

  async function confirmAction(msgId: string) {
    const cardIdx = messages.findIndex(m => m.id === msgId); // Fix 10: stable ID lookup
    if (cardIdx === -1) return;
    const card = messages[cardIdx] as ActionCardMessage;
    if (card.state !== "pending") return;

    setConfirmingId(msgId);
    setLoading(true);
    try {
      const r = await api.post("/api/chat/action", {
        action_type: card.action_type,
        params: card.params,
      });

      const summary: string = r.data.summary;

      setMessages(prev => prev.map(m =>
        m.id === msgId
          ? { ...m as ActionCardMessage, state: "confirmed", result_summary: summary } as ActionCardMessage
          : m
      ));

      // Fix 9: inject confirmed result into history so follow-up Q&A has context
      setMessages(prev => [...prev, {
        id: newId(), kind: "text", role: "assistant",
        content: summary,
      }]);

      if (r.data.remaining != null) {
        setQuota(q => q ? { ...q, used: r.data.used, remaining: r.data.remaining } : null);
      }
    } catch (e: any) {
      if (_errCode(e) === "plan_required") setPlanRestricted(true);
      const detail = _errDetail(e, "Action failed. Please try again.");
      setMessages(prev => prev.map(m =>
        m.id === msgId
          ? { ...m as ActionCardMessage, state: "error", result_summary: `⚠️ ${detail}` } as ActionCardMessage
          : m
      ));
    } finally {
      setLoading(false);
      setConfirmingId(null);
    }
  }

  function cancelAction(msgId: string) {
    setMessages(prev => prev.map(m =>
      m.id === msgId
        ? { ...m as ActionCardMessage, state: "cancelled", result_summary: "Got it — action cancelled." } as ActionCardMessage
        : m
    ));
  }

  const suggestions = selectSuggestions(role, modules, activeModule);
  const greeting = firstName
    ? `Hi ${firstName} 👋 I'm **Aria**, your HR assistant. Ask me anything or tell me to take an action.`
    : `Hi there 👋 I'm **Aria**, your HR assistant. Ask me anything or tell me to take an action.`;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleFabClick}
        onPointerDown={handleFabPointerDown}
        onPointerMove={handleFabPointerMove}
        onPointerUp={handleFabPointerUp}
        onPointerCancel={handleFabPointerUp}
        style={{
          position: "fixed", bottom: fabPos.bottom, right: fabPos.right, zIndex: 1300,
          width: 56, height: 56, borderRadius: "50%", border: "none",
          background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
          color: "white", fontSize: 22, cursor: "grab",
          boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.2s",
          overflow: "visible",
          touchAction: "none",
        }}
        title="Chat with Aria (drag to move)"
        aria-label={open ? "Close Aria" : "Open Aria"}
      >
        {open ? "✕" : <img src="/aria-avatar.png" alt="Aria" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />}
        {!open && ariaUnread > 0 && (
          <span style={{
            position: "absolute", top: 4, right: 4,
            width: 12, height: 12, borderRadius: "50%",
            background: "#ef4444", border: "2px solid white",
            display: "block",
          }} />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: "fixed", bottom: panelBottom, right: panelRight, zIndex: 1299,
          width: panelWidth, maxWidth: "calc(100vw - 32px)", height: panelHeight,
          background: "white", borderRadius: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column",
          fontFamily: "var(--font-sans)", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY}, #1e40af)`,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <img src="/aria-avatar.png" alt="Aria" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,0.3)" }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>Aria</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                {isUnlimited ? "Unlimited · Ask or act" : quota
                  ? `${quota.remaining ?? 0} of ${quota.limit} questions left today`
                  : "Your HR Assistant"}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 18, padding: 4 }}
              aria-label="Close"
            >✕</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", padding: "12px 14px",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            {messages.length === 0 && (
              <div>
                {/* Personalized greeting */}
                <div style={{
                  background: "#f1f5f9", borderRadius: "14px 14px 14px 4px",
                  padding: "10px 14px", marginBottom: 12,
                }}>
                  <AriaMarkdown text={greeting} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {suggestions.map(s => (
                    <button key={s.text} onClick={() => send(s.text)} style={{
                      background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
                      padding: "8px 12px", textAlign: "left", cursor: "pointer",
                      fontSize: 12, color: "#334155", lineHeight: 1.4,
                      transition: "background 0.15s",
                    }}>
                      {s.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => {
              if (m.kind === "action_card") {
                return (
                  <div key={m.id} style={{ display: "flex", justifyContent: "flex-start" }}>
                    <ActionCard
                      msg={m}
                      onConfirm={() => confirmAction(m.id)}
                      onCancel={() => cancelAction(m.id)}
                      confirming={confirmingId === m.id}
                    />
                  </div>
                );
              }
              return (
                <div key={m.id} style={{
                  display: "flex",
                  justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                }}>
                  <div style={{
                    maxWidth: "82%",
                    background: m.role === "user"
                      ? `linear-gradient(135deg, ${NAVY}, #1e40af)`
                      : "#f1f5f9",
                    color: m.role === "user" ? "white" : undefined,
                    borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    padding: "10px 14px",
                  }}>
                    {m.role === "user"
                      ? <span style={{ fontSize: 13, lineHeight: 1.5 }}>{m.content}</span>
                      : <AriaMarkdown text={m.content} />
                    }
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{
                  background: "#f1f5f9", borderRadius: "14px 14px 14px 4px",
                  padding: "12px 16px", display: "flex", gap: 4, alignItems: "center",
                }}>
                  {/* Fix 11: stagger via className, not inline animation override */}
                  <span className="aria-dot" />
                  <span className="aria-dot aria-dot--2" />
                  <span className="aria-dot aria-dot--3" />
                </div>
              </div>
            )}

            {/* Fix 8: pending card warning */}
            {hasPendingCard && !loading && (
              <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", padding: "4px 0" }}>
                Please respond to the action above before sending a new message.
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: "10px 12px", borderTop: "1px solid #e2e8f0",
            display: "flex", gap: 8, alignItems: "center",
          }}>
            {planRestricted ? (
              <div style={{ flex: 1, textAlign: "center", padding: "6px 0" }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                  Your account is in read-only mode. Upgrade to keep chatting with Aria.
                </div>
                <a
                  href="/settings?tab=usage"
                  style={{
                    display: "inline-block", fontSize: 12, fontWeight: 600, color: "white",
                    background: NAVY, borderRadius: 8, padding: "6px 14px", textDecoration: "none",
                  }}
                >
                  Upgrade Plan
                </a>
              </div>
            ) : outOfQuota ? (
              <div style={{ flex: 1, textAlign: "center", fontSize: 12, color: "#94a3b8", padding: "8px 0" }}>
                Daily limit reached. Come back tomorrow!
              </div>
            ) : (
              <>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
                  placeholder={hasPendingCard ? "Respond to the action above first…" : "Ask Aria anything…"}
                  disabled={loading || hasPendingCard}
                  style={{
                    flex: 1, padding: "9px 12px", borderRadius: 10, fontSize: 13,
                    border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b",
                    background: (loading || hasPendingCard) ? "#f8fafc" : "white",
                  }}
                />
                <button
                  onClick={() => send()}
                  disabled={!input.trim() || loading || hasPendingCard}
                  style={{
                    width: 36, height: 36, borderRadius: "50%", border: "none",
                    background: (!input.trim() || loading || hasPendingCard) ? "#e2e8f0" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                    color: (!input.trim() || loading || hasPendingCard) ? "#94a3b8" : "white",
                    cursor: (!input.trim() || loading || hasPendingCard) ? "not-allowed" : "pointer",
                    fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}
                  aria-label="Send"
                >
                  ➤
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        /* Fix 11: dot animation via proper class-based stagger */
        .aria-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: #94a3b8;
          animation: aria-pulse 1.2s ease-in-out infinite;
        }
        .aria-dot--2 { animation-delay: 0.2s; }
        .aria-dot--3 { animation-delay: 0.4s; }
        @keyframes aria-pulse {
          0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
          40%            { opacity: 1;    transform: scale(1);   }
        }
      `}</style>
    </>
  );
}
